import { Form, Formik } from 'formik'
import React from 'react'
import { Columns } from 'react-bulma-components'
import { useUpdateMutation } from '../../../../queries/work-periods'
import { dateToDateInputValue } from '../../../../utils/date'
import { RequestButton, RequestMessage } from '../../../request-components/request-components'
import WorkPeriodClockingTimeDetails from './work-period-clocking-time-details'
import WorkPeriodManualBreak from './work-period-manual-break'
import {
  buildFormInitialValues,
  buildSubmitPayload,
  calculateManualClockingDateFromFormValues,
  isManualClockingUpdated,
  validateFormValues,
} from './utils'
import { WorkPeriodDatesFormProps, WorkPeriodDatesFormValues } from './types'

/**
 * Composant principal pour la gestion des dates de période de travail
 * Permet la modification des heures de début/fin et des pauses manuelles
 */
const WorkPeriodDatesForm: React.FC<WorkPeriodDatesFormProps> = ({ workPeriod, disabled }) => {
  // Mutations
  const updateWorkPeriodMutation = useUpdateMutation()

  const handleSubmit = (values: WorkPeriodDatesFormValues) => {
    const payload = buildSubmitPayload(workPeriod, values)
    updateWorkPeriodMutation.mutate(payload)
  }

  const initialValues = buildFormInitialValues(workPeriod)

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={values => validateFormValues(workPeriod, values)}
      onSubmit={handleSubmit}
    >
      {props => {
        const clockingDates = React.useMemo(
          () => calculateManualClockingDateFromFormValues(workPeriod, props.values),
          [props.values],
        )

        const setManualClockingDay = (clockingTime: 'start' | 'end') => {
          const currentValue = props.values?.workPeriod[clockingTime].isCustomDay
          props.setFieldValue(`workPeriod.${clockingTime}.isCustomDay`, !currentValue)
          if (!currentValue) {
            props.setFieldValue(
              `workPeriod.${clockingTime}.manualClockingDay`,
              dateToDateInputValue(
                new Date(clockingDates?.[clockingTime] || workPeriod[clockingTime].date),
              ),
            )
          }
        }

        const manualClockingUpdated = isManualClockingUpdated(workPeriod, props.values)

        return (
          <Form>
            <Columns>
              <Columns.Column size={4}>
                <WorkPeriodClockingTimeDetails
                  workPeriod={workPeriod}
                  clockingTime="start"
                  formManualClockingValue={props.values?.workPeriod.start.manualClocking}
                  formTheoreticalValue={props.values?.workPeriod.start.date}
                  manualClockingDate={clockingDates?.start}
                  onEditManualClockingDay={() => setManualClockingDay('start')}
                  manualClockingUpdated={manualClockingUpdated}
                  displayManualClockingDay={props.values?.workPeriod.start.isCustomDay}
                  disabled={disabled}
                />
              </Columns.Column>
              <Columns.Column size={4}>
                <WorkPeriodClockingTimeDetails
                  workPeriod={workPeriod}
                  clockingTime="end"
                  formManualClockingValue={props.values?.workPeriod.end.manualClocking}
                  formTheoreticalValue={props.values?.workPeriod.end.date}
                  manualClockingDate={clockingDates?.end}
                  onEditManualClockingDay={() => setManualClockingDay('end')}
                  manualClockingUpdated={manualClockingUpdated}
                  displayManualClockingDay={props.values?.workPeriod.end.isCustomDay}
                  disabled={disabled}
                />
              </Columns.Column>
              <Columns.Column size={4}>
                <WorkPeriodManualBreak workPeriod={workPeriod} />
              </Columns.Column>
              <Columns.Column>
                {props.dirty && workPeriod.__actions.canBeUpdated && (
                  <>
                    <RequestButton
                      mutation={updateWorkPeriodMutation}
                      color="primary"
                      type="submit"
                      mr={3}
                      style={{ width: '100%' }}
                      data-test="work-period-dates-form-submit"
                    >
                      Modifier la journée {props.status}
                    </RequestButton>
                    <RequestMessage mutation={updateWorkPeriodMutation} />
                  </>
                )}
              </Columns.Column>
            </Columns>
          </Form>
        )
      }}
    </Formik>
  )
}

export default WorkPeriodDatesForm
